<template>
    <v-card class="overflow-hidden pt-20">
        <v-app-bar
            absolute
            dense
            color="#e0e0e0"
            elevate-on-scroll
            scroll-target="#scrolling-techniques-7"
        >
            <v-toolbar-title class="text-h6">Filters</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn icon @click="$emit('handleFacetModal')">
                <fa :icon="['fal', 'times']" class="fa-lg" />
            </v-btn>
        </v-app-bar>
        <v-sheet
            id="scrolling-techniques-7"
            class="overflow-y-aut mt-12"
            max-height="75vh"
        >
            <v-container v-if="responseData">
                <v-row>
                    <v-col
                        v-for="f in filtersToRender"
                        :key="f.name"
                        cols="12"
                        sm="4"
                    >
                        <v-select
                            :value="f.value"
                            outlined
                            hide-details
                            :items="f.list"
                            :label="f.name"
                            height="40"
                            multiple
                            chips
                            dense
                            :clearable="true"
                            ref="attrSelector"
                            @change="(val) => setFacetValues(f, val)"
                        >
                            <template v-slot:selection="data">
                                <v-chip v-if="data.index === 0">
                                    {{ data.item.value }}
                                </v-chip>
                                <span
                                    v-if="data.index === 1"
                                    class="grey--text text-caption"
                                >
                                    (+{{ f.value && f.value.length - 1 }}
                                    others)
                                </span>
                            </template>
                            <template v-slot:item="{ item, attrs, on }">
                                <v-list-item
                                    v-on="on"
                                    v-bind="attrs"
                                    #default="{ active }"
                                >
                                    <v-list-item-action>
                                        <v-checkbox
                                            :input-value="active"
                                        ></v-checkbox>
                                    </v-list-item-action>
                                    <v-list-item-content>
                                        <v-list-item-title>
                                            {{ item.value }}
                                        </v-list-item-title>
                                    </v-list-item-content>
                                    <v-list-item-action class="ma-0">
                                        <span> {{ item.text }} </span>
                                    </v-list-item-action>
                                </v-list-item>
                            </template>
                        </v-select>
                    </v-col>
                </v-row>
            </v-container>
            <v-container v-else>
                <v-row>
                    <v-col cols="12" sm="3">
                        <v-skeleton-loader type="text"></v-skeleton-loader
                    ></v-col>
                    <v-col cols="12" sm="3">
                        <v-skeleton-loader type="text"></v-skeleton-loader
                    ></v-col>
                    <v-col cols="12" sm="3">
                        <v-skeleton-loader type="text"></v-skeleton-loader
                    ></v-col>
                    <v-col cols="12" sm="3">
                        <v-skeleton-loader type="text"></v-skeleton-loader
                    ></v-col>
                </v-row>
            </v-container>
            <v-container v-show="!loading && filtersToRender.length === 0">
                <div class="text-center py-4">
                    <TranslatableStringComponent
                        :tstring="{ string: 'noFiltersCurrentlyAvailable' }"
                    />
                </div>
            </v-container>
        </v-sheet>
        <v-banner
            v-if="responseData"
            sticky
            style="background-color: #fff; bottom: 0"
        >
            <div class="d-flex justify-end">
                <v-btn text color="primary" small @click="clearAllFilters()"
                    ><TranslatableStringComponent
                        :tstring="{ string: 'clearAll' }"
                /></v-btn>
                <v-btn text small color="primary" @click="applyFilters()"
                    ><TranslatableStringComponent
                        :tstring="{ string: 'apply' }"
                /></v-btn>
            </div>
        </v-banner>
    </v-card>
</template>

<script>
import { mapState } from 'vuex'
import TranslatableStringComponent from '~/components/TranslatableStringComponent.vue'
export default {
    name: 'ShopFacets',
    components: { TranslatableStringComponent },
    props: {
        facets: {
            type: Array,
            default: () => [],
        },
        validFacets: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            responseData: null,
            loading: false,
            selectedFacets: [],
        }
    },
    async mounted() {
        if (this.decodedFilterValues.length > 0)
            this.selectedFacets = this.decodedFilterValues
        await this.fetchAttributeValues()
    },
    computed: {
        filtersToRender() {
            return this.validFacets.map((i) => {
                const list = this.responseData
                    ? this.responseData[i.title.en][i.title.en].buckets.map(
                          (m) => {
                              return {
                                  text: m.doc_count,
                                  value: m.key,
                              }
                          }
                      )
                    : []
                const value =
                    this.selectedFacets.find((k) => k.name === i.title.en)
                        ?.value || []

                return {
                    name: i.title.en,
                    list,
                    value,
                }
            })
        },
        getAgg() {
            const aggs = this.validFacets.map((f) => {
                const filter = this.getQueryByFilter(f.title.en)
                return {
                    [`${f.title.en}`]: {
                        filter,
                        aggs: {
                            [`${f.title.en}`]: {
                                terms: {
                                    field: `attributes.${f.title.en}.keyword`,
                                    size: 999,
                                    order: { _key: 'asc' },
                                },
                            },
                        },
                    },
                }
            })
            if (aggs.length === 0) return {}
            return {
                size: 999,
                aggs: aggs.reduce((objAll, obj) => ({ ...objAll, ...obj }), {}),
            }
        },
        decodedFilterValues() {
            const decodedFilterObj =
                this.$route.query.filter &&
                this.isBase64(this.$route.query.filter)
                    ? JSON.parse(window.atob(this.$route.query.filter))
                    : null

            return decodedFilterObj
                ? Object.entries(decodedFilterObj).map(([k, v]) => ({
                      name: k,
                      value: v,
                  }))
                : []
        },
        selectedCategory() {
            return this.$route.query.Categories
        },
    },

    methods: {
        async fetchAttributeValues() {
            this.loading = true
            const res = await this.$axios.post('elastic-search', {
                elasticQuery: {
                    size: 0,
                    ...this.getAgg,
                },
                elasticIndex: 'products-sanity',
            })
            this.responseData = res.data.aggregations
            this.loading = false
        },
        getQueryByFilter(excludeFacets) {
            const attributeTerms = this.selectedFacets
                .filter((f) => f.name !== excludeFacets)
                .filter((f) => f.value.length > 0)
                .map((f) => {
                    return {
                        terms: {
                            [`attributes.${f.name}.keyword`]: f.value,
                        },
                    }
                })
            const categoryTerms = {
                terms: {
                    categories: [this.selectedCategory],
                },
            }
            const accessoriesTerms = {
                terms: {
                    categories: [`${this.selectedCategory} Accessories`],
                },
            }
            const mustTerms = [categoryTerms, ...attributeTerms]
            return {
                bool: {
                    must: mustTerms,
                    filter: [
                        {
                            term: {
                                isPartial: false,
                            },
                        },
                    ],
                },
            }
        },
        isBase64(str) {
            if (typeof str !== 'string' && !process.client) {
                return false
            }
            try {
                return window.btoa(window.atob(str)) == str
            } catch (err) {
                return false
            }
        },
        applyFilters() {
            this.$emit('handleFacetModal')
            const selectedFacetsObj = this.selectedFacets.reduce(
                (obj, item) => ({ ...obj, [item.name]: item.value }),
                {}
            )
            const query =
                this.selectedFacets.length > 0
                    ? {
                          Categories: this.$route.query.Categories,
                          filter: window.btoa(
                              JSON.stringify(selectedFacetsObj)
                          ),
                      }
                    : {
                          Categories: this.$route.query.Categories,
                      }
            this.$router.push({
                query,
            })
        },
        setFacetValues(f, facetValue) {
            if (this.selectedFacets.find((s) => s.name === f.name)) {
                this.selectedFacets = this.selectedFacets.map((m) => {
                    if (m.name === f.name) {
                        return { ...m, value: facetValue }
                    } else {
                        return m
                    }
                })
            } else {
                this.selectedFacets = this.selectedFacets = [
                    ...this.selectedFacets,
                    { ...f, value: facetValue },
                ]
            }
            this.fetchAttributeValues()
        },
        clearAllFilters() {
            if (this.selectedFacets.length > 0) {
                this.selectedFacets = []
                this.fetchAttributeValues()
            }
        },
    },
}
</script>

<style lang="scss" scoped>
.v-chip {
    height: 18px !important;
    padding: 0 8px !important;
    border-radius: 14px !important;
}
</style>
